import type { ReactNode } from 'react';
import { Button } from '@paige/ui';
import { useFormat, type Formatter } from '@paige/format';
import type { FlowLinks } from '../flow/FlowDetail';
import { OpsCompanyName } from '../ops/OpsCompanyName';
import type { PendingAction } from './ProblemActionDialog';
import { ProblemFiles } from './ProblemFiles';
import type { ProblemFile, ProblemFilesPage, ProblemGroup } from './problem-types';
import {
  actionWords,
  countWords,
  groupHeadline,
  jobWords,
  nextStep,
  placeWords,
  type NextStep,
} from './problem-words';

export interface ProblemRowProps {
  group: ProblemGroup;
  links: FlowLinks;
  open: boolean;
  onToggle: () => void;
  loadFiles: (group: ProblemGroup, offset: number) => Promise<ProblemFilesPage>;
  /** Changes when the screen has read again, so an open list reads again too. */
  stamp: string;
  /** Opens the confirm step; absent for a role that may not act. */
  onAsk?: ((pending: PendingAction) => void) | undefined;
  /** Opens a file in the support viewer (Stages and queues' incident panel); absent where there is none. */
  onSeeFile?: ((group: ProblemGroup, file: ProblemFile) => void) | undefined;
}

type Tone = NextStep['tone'] | 'known';

const MARK: Record<Tone, string> = {
  customer: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm-7 8c.6-3.6 3.4-5.5 7-5.5s6.4 1.9 7 5.5',
  known: 'M5 12.5l4.5 4.5L19 7.5',
  support: 'M12 3.5 21.5 20h-19L12 3.5Zm0 6.5v4.5m0 2.6v.1',
  engineering: 'M12 3.5 21.5 20h-19L12 3.5Zm0 6.5v4.5m0 2.6v.1',
};

/** The mark in the row's corner: whose move it is, by shape and colour, never colour alone. */
function Mark({ tone }: { tone: Tone }): ReactNode {
  return (
    <svg className="pb-row__mark" viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
      <path
        d={MARK[tone]}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

interface PartProps {
  group: ProblemGroup;
  f: Formatter;
  onAsk: ProblemRowProps['onAsk'];
}

function Acknowledged({ group, f, onAsk }: PartProps): ReactNode {
  const ack = group.acknowledged;
  if (!ack) return null;
  const until = ack.until ? `until ${f.date(ack.until)}` : 'until taken back';
  return (
    <p className="pb-row__ack" data-ack={ack.open ? 'open' : 'covers'}>
      <strong>{ack.open ? 'Acknowledged, but more has parked since' : 'Acknowledged'}</strong>{' '}
      {until}
      {ack.by ? ` by ${ack.by}` : ''}: {ack.note}{' '}
      {onAsk ? (
        <button
          type="button"
          className="pb-link"
          onClick={() => onAsk({ group, action: 'unacknowledge' })}
        >
          Stop acknowledging
        </button>
      ) : null}
    </p>
  );
}

/** The row's controls: every staff action that exists for it, or one line saying who can. */
function Controls({ group, onAsk }: Omit<PartProps, 'f'>): ReactNode {
  if (group.can.length === 0) return null;
  if (!onAsk)
    return <span className="pb-row__noact">Support and engineering can act on this.</span>;
  const known = group.acknowledged !== null && !group.acknowledged.open;
  const shown = known ? group.can.filter((a) => a !== 'acknowledge') : group.can;
  // The customer's move is not ours to retry first: closing it is the lead action there.
  const lead = group.owner === 'customer' && shown.includes('resolve') ? 'resolve' : shown[0];
  return (
    <span className="pb-row__actions">
      {shown.map((action) => (
        <Button
          key={action}
          size="sm"
          variant={action === lead ? 'primary' : 'secondary'}
          onClick={() => onAsk({ group, action })}
        >
          {actionWords(group, action).all}
        </Button>
      ))}
    </span>
  );
}

/** What stopped, whose it is and where, and how much for how long. */
function Head({
  group: g,
  links,
  tone,
}: {
  group: ProblemGroup;
  links: FlowLinks;
  tone: Tone;
}): ReactNode {
  const f = useFormat();
  return (
    <div className="pb-row__head">
      <Mark tone={tone} />
      <div className="pb-row__what">
        <h3 className="pb-row__title">{groupHeadline(g)}</h3>
        <p className="pb-row__where">
          <OpsCompanyName
            company={g.company}
            href={links.company(g.company.slug)}
            Link={links.Link}
          />
          <span>{jobWords(g)}</span>
          <span>{placeWords(g.places)}</span>
        </p>
      </div>
      <div className="pb-row__count">
        <strong>{countWords(f, g)}</strong>
        <span>
          oldest {f.duration(g.oldestSeconds)}
          {g.files > 1 ? `, newest ${f.duration(g.newestSeconds)}` : ''}
        </span>
      </div>
    </div>
  );
}

function toggleWords(f: Formatter, open: boolean, files: number): string {
  if (open) return 'Hide the files';
  return files === 1 ? 'Show the file' : `Show the ${f.int(files)} files`;
}

/**
 * One row: what stopped in plain words, whose it is (with the Test mark), how much and for how long, whose move
 * it is, and the controls. It opens to its files.
 */
export function ProblemRow(props: ProblemRowProps): ReactNode {
  const { group: g, links, open, onToggle, onAsk } = props;
  const f = useFormat();
  const step = nextStep(g);
  const known = g.acknowledged !== null && !g.acknowledged.open;
  const tone: Tone = known ? 'known' : step.tone;
  const later =
    g.superseded > 0 ? ` A later send already delivered ${f.int(g.superseded)} of these.` : '';
  return (
    <li className="pb-row" data-group={g.id} data-owner={tone} data-open={open}>
      <Head group={g} links={links} tone={tone} />
      <div className="pb-row__step">
        <p className="pb-row__next">
          <span className={`pb-who pb-who--${step.tone}`}>{step.who}</span> {step.line}
          {later}
        </p>
        <Controls group={g} onAsk={onAsk} />
      </div>
      <Acknowledged group={g} f={f} onAsk={onAsk} />
      <button type="button" className="pb-row__toggle" aria-expanded={open} onClick={onToggle}>
        {toggleWords(f, open, g.files)}
      </button>
      {open ? <ProblemFiles {...props} /> : null}
    </li>
  );
}
